import { Controller } from "@hotwired/stimulus"

// Flat filter list where each row after the first begins with an and/or dropdown.
// Consecutive "and" rows form a group; "or" starts a new group. Serialized as a
// two-level answer_filters tree that Forms::RecipientAnswerFilter evaluates.
export default class extends Controller {
  static targets = ["rowsContainer", "rowTemplate"]

  static values = {
    applyUrl: String,
    initialFilters: Object
  }

  connect() {
    if (this.rowsContainerTarget.children.length === 0) {
      this.hydrateFromInitialFilters()
    }
  }

  hydrateFromInitialFilters() {
    const rows = this.rowsFromTree(this.initialFiltersValue)

    if (rows.length === 0) {
      this.addFilter()
      return
    }

    rows.forEach((row) => {
      this.rowsContainerTarget.appendChild(this.buildFilterRow(row))
    })
    this.syncRowPrefixes()
  }

  // Flattens a (max two-level) tree back into ordered rows with combinators.
  rowsFromTree(tree) {
    if (!tree || !Array.isArray(tree.filters) || tree.filters.length === 0) return []

    const groups = tree.match === "or"
      ? tree.filters.map((node) => (node.filters ? node.filters : [node]))
      : [tree.filters]

    const rows = []
    groups.forEach((groupConditions, groupIndex) => {
      groupConditions.forEach((node, conditionIndex) => {
        if (node.filters) return // deeper nesting isn't editable in this UI

        rows.push({
          combinator: groupIndex === 0 && conditionIndex === 0 ? null : (conditionIndex === 0 ? "or" : "and"),
          question_id: node.question_id,
          operator: node.operator,
          value: node.value
        })
      })
    })

    return rows
  }

  addFilter(event) {
    event?.preventDefault()
    this.rowsContainerTarget.appendChild(this.buildFilterRow({ combinator: "and" }))
    this.syncRowPrefixes()
  }

  removeFilter(event) {
    event.preventDefault()
    const filterRow = event.target.closest("[data-filter-row]")
    if (!filterRow) return

    filterRow.remove()

    if (this.rowsContainerTarget.children.length === 0) {
      this.addFilter()
      return
    }

    this.syncRowPrefixes()
  }

  operatorChanged(event) {
    const filterRow = event.target.closest("[data-filter-row]")
    if (filterRow) this.syncValueVisibility(filterRow)
  }

  clearFilters(event) {
    event.preventDefault()
    this.rowsContainerTarget.innerHTML = ""
    this.addFilter()
  }

  save(event) {
    event.preventDefault()

    const tree = this.serializeTree()
    const url = new URL(this.applyUrlValue, window.location.origin)

    if (tree) {
      url.searchParams.set("answer_filters", JSON.stringify(tree))
      url.searchParams.delete("clear_answer_filters")
    } else {
      // Explicit clear so sticky session filters are dropped (omitting the param would restore them).
      url.searchParams.delete("answer_filters")
      url.searchParams.set("clear_answer_filters", "1")
    }

    window.location = url.toString()
  }

  buildFilterRow(rowData = {}) {
    const filterRow = this.rowTemplateTarget.content.firstElementChild.cloneNode(true)

    const combinatorSelect = filterRow.querySelector("[data-row-combinator]")
    if (combinatorSelect) combinatorSelect.value = rowData.combinator === "or" ? "or" : "and"

    const questionSelect = filterRow.querySelector("[data-filter-question]")
    if (questionSelect) questionSelect.value = String(rowData.question_id || "")

    const operatorSelect = filterRow.querySelector("[data-filter-operator]")
    if (operatorSelect) operatorSelect.value = rowData.operator || "contains"

    const valueInput = filterRow.querySelector("[data-filter-value]")
    if (valueInput) valueInput.value = rowData.value || ""

    this.syncValueVisibility(filterRow)
    return filterRow
  }

  // First row shows "Where"; later rows show the and/or SelectComponent instead.
  syncRowPrefixes() {
    Array.from(this.rowsContainerTarget.children).forEach((filterRow, index) => {
      const whereLabel = filterRow.querySelector("[data-row-where]")
      const combinatorSelect = filterRow.querySelector("[data-row-combinator]")
      const combinatorComponent = combinatorSelect?.closest(".fanword-select-component")

      if (whereLabel) {
        whereLabel.classList.toggle("d-none", index !== 0)
      }

      // Hide the whole SelectComponent (select + chevron), not just the <select>.
      if (combinatorComponent) {
        combinatorComponent.classList.toggle("d-none", index === 0)
      }
    })
  }

  syncValueVisibility(filterRow) {
    const operatorSelect = filterRow.querySelector("[data-filter-operator]")
    const valueWrapper = filterRow.querySelector("[data-filter-value-wrapper]")
    if (!operatorSelect || !valueWrapper) return

    const operator = operatorSelect.value
    valueWrapper.classList.toggle("d-none", operator === "is_empty" || operator === "is_not_empty")
  }

  serializeTree() {
    const rows = []

    Array.from(this.rowsContainerTarget.querySelectorAll("[data-filter-row]")).forEach((filterRow, index) => {
      const questionId = filterRow.querySelector("[data-filter-question]")?.value
      const operator = filterRow.querySelector("[data-filter-operator]")?.value
      const value = filterRow.querySelector("[data-filter-value]")?.value
      const combinator = index === 0 ? null : filterRow.querySelector("[data-row-combinator]")?.value

      if (!questionId || !operator) return

      const condition = { question_id: Number(questionId), operator }
      if (operator !== "is_empty" && operator !== "is_not_empty") {
        if (!value || !String(value).trim()) return
        condition.value = String(value).trim()
      }

      rows.push({ combinator, condition })
    })

    if (rows.length === 0) return null

    // "or" starts a new group; consecutive "and" rows stay in the current group.
    const groups = []
    let currentGroup = []
    rows.forEach((row, index) => {
      if (index > 0 && row.combinator === "or" && currentGroup.length > 0) {
        groups.push(currentGroup)
        currentGroup = []
      }
      currentGroup.push(row.condition)
    })
    groups.push(currentGroup)

    if (groups.length === 1) {
      return { match: "and", filters: groups[0] }
    }

    return {
      match: "or",
      filters: groups.map((groupConditions) => ({ match: "and", filters: groupConditions }))
    }
  }
}
